import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { CheckIcon, CloseIcon } from './icons';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

// ---------------------------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------------------------

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    block,
    loading,
    icon,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        'cfg-btn',
        `cfg-btn--${variant}`,
        size !== 'md' && `cfg-btn--${size}`,
        block && 'cfg-btn--block',
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <span className="cfg-spinner" style={{ width: 16, height: 16 }} aria-hidden />
      ) : (
        icon
      )}
      {children}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'children'> {
  label: string;
  icon: ReactNode;
}

export function IconButton({
  label,
  icon,
  className,
  variant = 'ghost',
  ...rest
}: IconButtonProps) {
  return (
    <Button
      {...rest}
      variant={variant}
      className={cx('cfg-icon-btn', className)}
      aria-label={label}
      title={label}
    >
      {icon}
    </Button>
  );
}

// ---------------------------------------------------------------------------------------------
// Segmented control (single choice)
// ---------------------------------------------------------------------------------------------

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  const selected = options.findIndex((o) => o.value === value);
  // ARIA radio group: the group is one tab stop and the arrow keys move the choice
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const buttons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')];
    const from = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (from < 0) return;
    const rtl = getComputedStyle(event.currentTarget).direction === 'rtl';
    const forwards = rtl ? -1 : 1;
    const step =
      event.key === 'ArrowRight'
        ? forwards
        : event.key === 'ArrowLeft'
          ? -forwards
          : event.key === 'ArrowDown' || event.key === 'End'
            ? 1
            : event.key === 'ArrowUp' || event.key === 'Home'
              ? -1
              : 0;
    if (!step) return;
    event.preventDefault();
    const ends = event.key === 'Home' || event.key === 'End';
    let index = event.key === 'Home' ? -1 : event.key === 'End' ? buttons.length : from;
    for (let n = 0; n < buttons.length; n++) {
      index += event.key === 'Home' ? 1 : event.key === 'End' ? -1 : step;
      if (!ends) index = (index + buttons.length) % buttons.length;
      const button = buttons[index];
      if (!button) return;
      if (button.disabled) continue;
      button.focus();
      onChange(options[index]!.value);
      return;
    }
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cx('cfg-segmented', className)}
      onKeyDown={onKeyDown}
    >
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          disabled={o.disabled}
          // the selected option is the tab stop (the first one when nothing matches)
          tabIndex={i === (selected < 0 ? 0 : selected) ? 0 : -1}
          onClick={() => onChange(o.value)}
        >
          {o.icon}
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Swatch
// ---------------------------------------------------------------------------------------------

export interface SwatchProps {
  image?: string;
  color?: string;
  label: string;
  selected: boolean;
  disabled?: boolean;
  loading?: boolean;
  badge?: ReactNode;
  description?: string;
  /** Roving tabindex: pass 0 for the one swatch of the group that is tabbable, -1 for the rest. */
  tabIndex?: number;
  onSelect: () => void;
  onPreview?: () => void;
}

export function Swatch({
  image,
  color,
  label,
  selected,
  disabled,
  loading,
  badge,
  description,
  tabIndex,
  onSelect,
  onPreview,
}: SwatchProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      aria-label={description ? `${label}, ${description}` : label}
      tabIndex={tabIndex}
      className="cfg-swatch"
      onClick={() => !disabled && onSelect()}
      onMouseEnter={onPreview}
      onFocus={onPreview}
    >
      <span
        className="cfg-swatch__image"
        style={{ backgroundImage: image ? `url("${image}")` : undefined, backgroundColor: color }}
      >
        {selected && (
          <span className="cfg-swatch__check">
            <CheckIcon size={14} strokeWidth={2.6} />
          </span>
        )}
        {badge && <span className="cfg-swatch__badge">{badge}</span>}
        {loading && (
          <span className="cfg-swatch__loading">
            <span className="cfg-spinner" />
          </span>
        )}
      </span>
      <span className="cfg-swatch__label">{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------------------------
// Badge, spinner, alert
// ---------------------------------------------------------------------------------------------

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'accent';
  children: ReactNode;
}) {
  return (
    <span className={cx('cfg-badge', tone !== 'neutral' && `cfg-badge--${tone}`)}>{children}</span>
  );
}

export function Spinner({ label, size = 20 }: { label?: string; size?: number }) {
  return (
    <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span className="cfg-spinner" style={{ width: size, height: size }} aria-hidden />
      {label ? <span>{label}</span> : <span className="cfg-visually-hidden">Loading</span>}
    </span>
  );
}

export function Alert({
  tone = 'info',
  children,
  icon,
}: {
  tone?: 'info' | 'warning' | 'error' | 'neutral';
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div
      className={cx('cfg-alert', tone !== 'neutral' && `cfg-alert--${tone}`)}
      role={tone === 'error' ? 'alert' : undefined}
    >
      {icon}
      <div>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------------------------

interface FieldShellProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
  className?: string;
}

export function Field({ label, hint, error, children, className }: FieldShellProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx('cfg-field', className)}>
      <label htmlFor={id} className="cfg-field__label">
        {label}
      </label>
      {children({ id, describedBy })}
      {hint && (
        <span id={hintId} className="cfg-field__hint">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className="cfg-field__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function TextField({
  label,
  hint,
  error,
  className,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}) {
  return (
    <Field label={label} hint={hint} error={error} className={className}>
      {({ id, describedBy }) => (
        <input
          id={id}
          className="cfg-input"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...input}
        />
      )}
    </Field>
  );
}

export function TextArea({
  label,
  hint,
  error,
  className,
  ...input
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}) {
  return (
    <Field label={label} hint={hint} error={error} className={className}>
      {({ id, describedBy }) => (
        <textarea id={id} className="cfg-textarea" aria-describedby={describedBy} {...input} />
      )}
    </Field>
  );
}

export function SelectField({
  label,
  hint,
  error,
  className,
  options,
  ...select
}: SelectHTMLAttributes<HTMLSelectElement> & {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  options: { value: string; label: string; disabled?: boolean }[];
}) {
  return (
    <Field label={label} hint={hint} error={error} className={className}>
      {({ id, describedBy }) => (
        <select
          id={id}
          className="cfg-select"
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          {...select}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

/**
 * Accessible names of the −/+ buttons of a `NumberStepper`, already translated, e.g.
 * `{ decrease: 'Decrease width by 10 mm', increase: 'Increase width by 10 mm' }`.
 */
export interface StepperButtonLabels {
  decrease: string;
  increase: string;
}

/** Numeric input with −/+ buttons, clamped and snapped on blur. */
export function NumberStepper({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
  hint,
  error,
  buttonLabels,
}: {
  label: ReactNode;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  hint?: ReactNode;
  error?: ReactNode;
  onChange: (value: number) => void;
  /** Names of the −/+ buttons; without it they are "−{step}" / "+{step}". */
  buttonLabels?: StepperButtonLabels;
}) {
  const names = buttonLabels ?? { decrease: `−${step}`, increase: `+${step}` };
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);
  const commit = (raw: number) => {
    if (!Number.isFinite(raw)) {
      setDraft(String(value));
      return;
    }
    const snapped = Math.round((Math.min(max, Math.max(min, raw)) - min) / step) * step + min;
    setDraft(String(snapped));
    if (snapped !== value) onChange(snapped);
  };
  return (
    <Field label={label} hint={hint} error={error}>
      {({ id, describedBy }) => (
        <div className="cfg-stepper">
          <button
            type="button"
            aria-label={names.decrease}
            disabled={value <= min}
            onClick={() => commit(value - step)}
          >
            −
          </button>
          <input
            id={id}
            inputMode="numeric"
            value={draft}
            aria-describedby={describedBy}
            aria-invalid={error ? true : undefined}
            onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ''))}
            onBlur={() => commit(Number(draft))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit(Number(draft));
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                commit(value + step);
              }
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                commit(value - step);
              }
            }}
          />
          {unit && (
            <span
              aria-hidden
              style={{
                alignSelf: 'center',
                paddingInlineEnd: 8,
                color: 'var(--cfg-muted)',
                fontSize: 13,
              }}
            >
              {unit}
            </span>
          )}
          <button
            type="button"
            aria-label={names.increase}
            disabled={value >= max}
            onClick={() => commit(value + step)}
          >
            +
          </button>
        </div>
      )}
    </Field>
  );
}

// ---------------------------------------------------------------------------------------------
// Dialog (native <dialog>)
// ---------------------------------------------------------------------------------------------

export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  closeLabel = 'Close',
  width,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
  width?: number;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="cfg-dialog"
      aria-labelledby={titleId}
      style={width ? { width: `min(${width}px, calc(100vw - 32px))` } : undefined}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      {open && (
        <>
          <div className="cfg-dialog__header">
            <h2 id={titleId} style={{ fontSize: 18 }}>
              {title}
            </h2>
            <IconButton label={closeLabel} icon={<CloseIcon />} onClick={onClose} />
          </div>
          <div className="cfg-dialog__body">{children}</div>
          {footer && <div className="cfg-dialog__footer">{footer}</div>}
        </>
      )}
    </dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------------------------

interface Toast {
  id: number;
  text: string;
  tone: 'info' | 'error';
}

const ToastContext = createContext<(text: string, tone?: 'info' | 'error') => void>(
  () => undefined,
);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);
  const push = useCallback((text: string, tone: 'info' | 'error' = 'info') => {
    const id = ++counter.current;
    setToasts((t) => [...t, { id, text, tone }]);
    setTimeout(
      () => setToasts((t) => t.filter((x) => x.id !== id)),
      tone === 'error' ? 6000 : 3500,
    );
  }, []);
  const value = useMemo(() => push, [push]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="cfg-toasts" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cx('cfg-toast', t.tone === 'error' && 'cfg-toast--error')}
            role={t.tone === 'error' ? 'alert' : 'status'}
          >
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
